/**
 * Auth provider — wraps the app with the AuthContext defined in
 * ./auth-context. On boot, if a token is in localStorage we call
 * /auth/me once to rehydrate the user; a 401 there clears tokens and
 * the router bounces the user to /login.
 *
 * The `useAuth` / `useHasRole` consumers live in ./useAuth so this
 * file stays a pure component module — Fast Refresh refuses to swap
 * a file that mixes a component with hooks/constants.
 */

import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { api, clearTokens, getAccessToken, saveTokens } from "./api";
import {
  AuthContext,
  type AuthState,
} from "./auth-context";
import type {
  AuthMeResponse,
  CreateOrgResponse,
  SwitchOrgResponse,
  TokenPair,
} from "./types";

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthMeResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const qc = useQueryClient();

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await api.get<AuthMeResponse>("/auth/me");
      setUser(data);
    } catch {
      clearTokens();
      setUser(null);
    }
  }, []);

  useEffect(() => {
    if (getAccessToken()) {
      void fetchMe().finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [fetchMe]);

  const login = useCallback(
    async (email: string, password: string) => {
      const { data } = await api.post<TokenPair>("/auth/login", { email, password });
      saveTokens(data);
      // Nuke any cache left over from a previous session on this
      // device — different user = different org scope.
      qc.clear();
      await fetchMe();
    },
    [fetchMe, qc]
  );

  const logout = useCallback(async () => {
    try {
      const refresh = localStorage.getItem("oneads.refresh_token");
      if (refresh) {
        await api.post("/auth/logout", { refresh_token: refresh });
      }
    } catch {
      // Logout is best-effort — even if the server call fails, clear
      // local state so the user isn't stuck.
    }
    clearTokens();
    // Drop cached org-scoped data so the next logged-in user never
    // sees the previous user's rows even for a blink.
    qc.clear();
    setUser(null);
  }, [qc]);

  const switchOrg = useCallback(
    async (orgId: number) => {
      const { data } = await api.post<SwitchOrgResponse>(
        "/auth/switch-org",
        { org_id: orgId }
      );
      // Re-use the existing refresh token; just swap the access token.
      saveTokens({
        access_token: data.access_token,
        refresh_token: localStorage.getItem("oneads.refresh_token") ?? "",
        token_type: "bearer",
        expires_at: data.expires_at,
      });
      // Clear every cached query — org scope changed, all stats must refetch.
      qc.clear();
      await fetchMe();
    },
    [fetchMe, qc]
  );

  const createOrg = useCallback(
    async (name: string, timezone?: string): Promise<CreateOrgResponse> => {
      const { data } = await api.post<CreateOrgResponse>("/auth/create-org", {
        name,
        timezone,
      });
      saveTokens({
        access_token: data.access_token,
        refresh_token: localStorage.getItem("oneads.refresh_token") ?? "",
        token_type: "bearer",
        expires_at: data.expires_at,
      });
      qc.clear();
      await fetchMe();
      return data;
    },
    [fetchMe, qc]
  );

  const value = useMemo<AuthState>(
    () => ({
      user,
      loading,
      login,
      logout,
      reload: fetchMe,
      switchOrg,
      createOrg,
    }),
    [user, loading, login, logout, fetchMe, switchOrg, createOrg]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
